import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useVersioningStore } from "@/stores/versioning/versioning-store";
import { VersionTimeline } from "@/components/versioning/VersionTimeline/VersionTimeline";
import { DraftManager } from "@/components/versioning/DraftManager/DraftManager";
import { BranchVisualization } from "@/components/versioning/BranchVisualization/BranchGraph";
import {
  GitBranch,
  FileText,
  Clock,
  X,
  Maximize2,
  LayoutGrid,
  History,
  Users,
} from "lucide-react";

interface FullPageVersioningInterfaceProps {
  onClose: () => void;
  className?: string;
}

export const FullPageVersioningInterface: React.FC<
  FullPageVersioningInterfaceProps
> = ({ onClose, className }) => {
  const { projectId } = useParams<{ projectId: string }>();
  const [activeTab, setActiveTab] = useState("timeline");
  const [selectedVersions, setSelectedVersions] = useState<string[]>([]);

  const { metadata, versions, drafts, branches, currentVersion, isLoading } =
    useVersioningStore();

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      // ESC to close versioning mode
      if (event.key === "Escape") {
        onClose();
      }
      // Ctrl/Cmd + 1-4 to switch tabs
      if (
        (event.ctrlKey || event.metaKey) &&
        event.key >= "1" &&
        event.key <= "4"
      ) {
        event.preventDefault();
        const tabIndex = parseInt(event.key) - 1;
        const tabs = ["timeline", "drafts", "branches", "comparison"];
        if (tabs[tabIndex]) {
          setActiveTab(tabs[tabIndex]);
        }
      }
    };

    document.addEventListener("keydown", handleKeyPress);
    return () => document.removeEventListener("keydown", handleKeyPress);
  }, [onClose]);

  return (
    <div
      className={cn(
        "fixed inset-0 bg-background z-40 flex flex-col",
        "animate-in fade-in-0 duration-300",
        className,
      )}
    >
      {/* Full-page versioning header */}
      <div className="flex items-center justify-between p-6 border-b border-border bg-muted/30">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <GitBranch className="w-6 h-6 text-primary" />
            <h1 className="text-2xl font-bold">Version Control</h1>
          </div>

          {/* Quick stats */}
          <div className="flex items-center space-x-4 ml-8">
            <div className="flex items-center space-x-2 px-3 py-1 bg-background rounded-md border">
              <History className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium">
                {metadata.totalVersions}
              </span>
              <span className="text-sm text-muted-foreground">Versions</span>
            </div>

            <div className="flex items-center space-x-2 px-3 py-1 bg-background rounded-md border">
              <FileText className="w-4 h-4 text-orange-600" />
              <span className="text-sm font-medium">
                {metadata.totalDrafts}
              </span>
              <span className="text-sm text-muted-foreground">Drafts</span>
            </div>

            <div className="flex items-center space-x-2 px-3 py-1 bg-background rounded-md border">
              <GitBranch className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium">{branches.length}</span>
              <span className="text-sm text-muted-foreground">Branches</span>
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          {/* Current version indicator */}
          <div className="flex items-center space-x-2">
            <span className="text-sm text-muted-foreground">Current:</span>
            <Badge variant="secondary" className="text-sm">
              v{currentVersion || 1}
            </Badge>
          </div>

          {/* Close button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
            aria-label="Exit version control mode (ESC)"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Main content area with full-width tabs */}
      <div className="flex-1 overflow-hidden">
        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="h-full flex flex-col"
        >
          {/* Enhanced tab navigation */}
          <div className="px-6 pt-4 pb-0">
            <TabsList className="grid w-full max-w-md grid-cols-4">
              <TabsTrigger
                value="timeline"
                className="flex items-center space-x-2"
              >
                <Clock className="w-4 h-4" />
                <span>Timeline</span>
                {versions.length > 0 && (
                  <Badge variant="outline" className="text-xs ml-1">
                    {versions.length}
                  </Badge>
                )}
              </TabsTrigger>

              <TabsTrigger
                value="drafts"
                className="flex items-center space-x-2"
              >
                <FileText className="w-4 h-4" />
                <span>Drafts</span>
                {drafts.length > 0 && (
                  <Badge variant="outline" className="text-xs ml-1">
                    {drafts.length}
                  </Badge>
                )}
              </TabsTrigger>

              <TabsTrigger
                value="branches"
                className="flex items-center space-x-2"
              >
                <GitBranch className="w-4 h-4" />
                <span>Branches</span>
                {branches.length > 0 && (
                  <Badge variant="outline" className="text-xs ml-1">
                    {branches.length}
                  </Badge>
                )}
              </TabsTrigger>

              <TabsTrigger
                value="comparison"
                className="flex items-center space-x-2"
                disabled={selectedVersions.length < 2}
              >
                <LayoutGrid className="w-4 h-4" />
                <span>Compare</span>
                {selectedVersions.length > 0 && (
                  <Badge variant="outline" className="text-xs ml-1">
                    {selectedVersions.length}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Tab content with full screen utilization */}
          <div className="flex-1 overflow-hidden px-6 pb-6">
            <TabsContent value="timeline" className="h-full mt-4">
              <div className="h-full grid grid-cols-3 gap-6">
                {/* Left: Version Timeline */}
                <div className="col-span-2 h-full overflow-hidden">
                  <Card className="h-full">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center space-x-2">
                        <History className="w-5 h-5" />
                        <span>Version Timeline</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="h-full overflow-hidden p-0">
                      <VersionTimeline
                        projectId={projectId || ""}
                        displayMode="timeline"
                        showFilters={true}
                        showSearch={true}
                        className="border-0 shadow-none h-full"
                        onVersionSelect={(version) => {
                          console.log("Version selected:", version);
                          // TODO: Show version details in right panel
                        }}
                        onVersionRestore={(version) => {
                          console.log("Restoring version:", version.version);
                        }}
                        onVersionCompare={(versionA, versionB) => {
                          const newSelection = [
                            versionA.id,
                            versionB?.id,
                          ].filter(Boolean) as string[];
                          setSelectedVersions(newSelection);
                          if (newSelection.length >= 2) {
                            setActiveTab("comparison");
                          }
                        }}
                      />
                    </CardContent>
                  </Card>
                </div>

                {/* Right: Version Details & Actions */}
                <div className="h-full overflow-hidden">
                  <Card className="h-full">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg">Quick Actions</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Version creation */}
                      <div className="space-y-2">
                        <h4 className="font-medium">Create Version</h4>
                        <Button className="w-full" size="sm">
                          <History className="w-4 h-4 mr-2" />
                          Save Current State
                        </Button>
                      </div>

                      {/* Selection info */}
                      {selectedVersions.length > 0 && (
                        <div className="space-y-2">
                          <h4 className="font-medium">
                            Selected ({selectedVersions.length})
                          </h4>
                          <div className="space-y-1">
                            {selectedVersions.map((versionId) => {
                              const version = versions.find(
                                (v) => v.id === versionId,
                              );
                              return version ? (
                                <div
                                  key={versionId}
                                  className="text-sm p-2 bg-muted rounded"
                                >
                                  v{version.version}
                                </div>
                              ) : null;
                            })}
                          </div>
                          {selectedVersions.length >= 2 && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="w-full"
                              onClick={() => setActiveTab("comparison")}
                            >
                              Compare Selected
                            </Button>
                          )}
                        </div>
                      )}

                      {/* Recent activity */}
                      <div className="space-y-2">
                        <h4 className="font-medium">Recent Activity</h4>
                        <div className="text-sm text-muted-foreground">
                          {versions.length > 0 ? (
                            <div className="space-y-1">
                              {versions.slice(0, 3).map((version) => (
                                <div
                                  key={version.id}
                                  className="flex items-center justify-between"
                                >
                                  <span>v{version.version}</span>
                                  <span>
                                    {new Date(
                                      version.createdAt,
                                    ).toLocaleDateString()}
                                  </span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            "No versions yet"
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="drafts" className="h-full mt-4">
              <Card className="h-full">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center space-x-2">
                    <FileText className="w-5 h-5" />
                    <span>Draft Management</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="h-full overflow-hidden p-0">
                  <DraftManager
                    projectId={projectId || ""}
                    viewMode="grid"
                    showFilters={true}
                    showCreateButton={true}
                    className="border-0 shadow-none h-full"
                    onDraftSelect={(draft) => {
                      console.log("Draft selected:", draft);
                    }}
                    onDraftCreate={() => {
                      console.log("Creating new draft");
                    }}
                    onDraftUpdate={(draftId, updates) => {
                      console.log("Updating draft:", draftId, updates);
                    }}
                    onDraftPublish={(draftId) => {
                      console.log("Publishing draft:", draftId);
                    }}
                    onDraftDelete={(draftId) => {
                      console.log("Deleting draft:", draftId);
                    }}
                    onDraftReview={(draftId, action, comments) => {
                      console.log(
                        "Reviewing draft:",
                        draftId,
                        action,
                        comments,
                      );
                    }}
                  />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="branches" className="h-full mt-4">
              <Card className="h-full">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center space-x-2">
                    <GitBranch className="w-5 h-5" />
                    <span>Branch Management</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="h-full overflow-hidden">
                  {/* Branch visualization will go here */}
                  <div className="flex items-center justify-center h-full text-muted-foreground">
                    <div className="text-center space-y-2">
                      <GitBranch className="w-12 h-12 mx-auto" />
                      <p>Branch visualization coming soon</p>
                      <p className="text-sm">
                        Full-width branch graphs and merge visualization
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="comparison" className="h-full mt-4">
              <Card className="h-full">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center space-x-2">
                    <LayoutGrid className="w-5 h-5" />
                    <span>Version Comparison</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="h-full overflow-hidden">
                  {selectedVersions.length >= 2 ? (
                    <div className="flex items-center justify-center h-full text-muted-foreground">
                      <div className="text-center space-y-2">
                        <LayoutGrid className="w-12 h-12 mx-auto" />
                        <p>Side-by-side version comparison</p>
                        <p className="text-sm">
                          Comparing v
                          {selectedVersions
                            .map((id) => {
                              const version = versions.find((v) => v.id === id);
                              return version?.version;
                            })
                            .join(" vs v")}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-full text-muted-foreground">
                      <div className="text-center space-y-2">
                        <LayoutGrid className="w-12 h-12 mx-auto opacity-50" />
                        <p>Select 2 or more versions to compare</p>
                        <p className="text-sm">
                          Use the Timeline tab to select versions
                        </p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </div>
  );
};

export default FullPageVersioningInterface;
